/*
 * <featured-tabs> — the tab bar on the "Shop The World of MIRIQA" section.
 *
 * The WAI-ARIA tabs pattern: clicking or arrowing to a tab selects it, and only
 * the selected tab is in the page's tab order, so Tab moves past the whole bar
 * to the panel rather than through every tab in turn.
 *
 * Panels are rendered server-side and toggled with the `hidden` attribute; there
 * is no fetching, so switching is instant and works before any other script has
 * run. Nothing outside the section observes the selection, so this deliberately
 * skips the pub/sub in pubsub.js.
 *
 * Nothing here tells the sliders in a revealed panel to re-measure, and that is
 * deliberate: SliderComponent (global.js) observes its own <ul> with a
 * ResizeObserver, and initPages() filters slides on `clientWidth > 0`. A hidden
 * panel's slider therefore measures nothing and bails, then re-initialises on
 * its own the moment the panel gets a box. Don't add a resize nudge here.
 */
class FeaturedTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    if (this.tabs.length === 0) return;

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeyDown);
  }

  onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (tab && this.contains(tab)) this.select(tab);
  };

  onKeyDown = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;

    const index = this.tabs.indexOf(tab);
    let next;

    switch (event.key) {
      case 'ArrowRight':
        next = this.tabs[(index + 1) % this.tabs.length];
        break;
      case 'ArrowLeft':
        next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        break;
      case 'Home':
        next = this.tabs[0];
        break;
      case 'End':
        next = this.tabs[this.tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next);
    // Selection follows focus in this pattern, so move focus with it.
    next.focus();
  };

  select(tab) {
    this.tabs.forEach((current) => {
      const selected = current === tab;
      current.setAttribute('aria-selected', selected ? 'true' : 'false');
      // Roving tabindex: the selected tab is the bar's single tab stop.
      current.setAttribute('tabindex', selected ? '0' : '-1');

      const panel = document.getElementById(current.getAttribute('aria-controls'));
      if (panel) panel.toggleAttribute('hidden', !selected);
    });
  }
}

customElements.define('featured-tabs', FeaturedTabs);
